/* eslint-disable @next/next/no-img-element */
import dynamic from "next/dynamic";
import Link from "next/link";
import { FC, Fragment, Suspense, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import SearchIcon from "~/assets/svgs/search.svg";
import Button from "~/components/Button";
import Container from "~/components/Container";
import { recentKeywordState } from "~/services/recent-search/recent-search.atom";
import storeConfigAtom from "~/services/store-config/store-config.atom";
import SearchFilter from "../SearchFilter";
import styles from "./Header.module.scss";
import Image from "next/legacy/image";
import { UserAtom } from "~/services/user";
import { CartAtom } from "~/services/cart";
import Alert from "~/components/Alert";
import { NodesResponse } from "~/data/home/interface";
import useClient from "~/hooks/useClient";
import Navigation from "../Navigation";
import TopHeader from "~/components/TopHeader";
import classNames from "classnames";
import MobileHeaderTwoV2 from "./MobileHeaderTwoV2";
import BottomAlert from "~/components/BottomAlert";

const UserButton = dynamic(() => import("~/components/UserButton"), {
  ssr: false,
  loading(loadingProps) {
    return null; // or return a loading component
  },
});

const WishButton = dynamic(() => import("~/components/WishButton"), {
  ssr: false,
  loading(loadingProps) {
    return null; // or return a loading component
  },
});

const CartButton = dynamic(() => import("~/components/CartButton"), {
  ssr: false,
  loading(loadingProps) {
    return null; // or return a loading component
  },
});

interface HeaderProps {
  menu?: NodesResponse[];
  storeConfig?: {
    frontend_config_logo: string;
  };
}

const HeaderV2: FC<HeaderProps> = ({
  menu,
  storeConfig: storeConfigServerSide,
}) => {
  const isClient = useClient();
  const [openSearch, setOpenSearch] = useState(false);
  const recoilRecentKeywords = useRecoilValue(recentKeywordState);

  const [openMenu, setOpenMenu] = useState(false);
  const storeConfig = useRecoilValue(storeConfigAtom.store);
  const [recentKeywords, setRecentKeywords] = useState<string[]>();
  const currentUser = useRecoilValue(UserAtom.currentUser);
  const Cart = useRecoilValue(CartAtom.cartSelector);

  const handleOpenSearch = () => {
    setTimeout(() => {
      setOpenSearch(!openSearch);
    }, 0);
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      setRecentKeywords(recoilRecentKeywords);
    }
  }, [recoilRecentKeywords]);

  return (
    <Fragment>
      <div
        className={classNames("z-20 relative min-h-[3.125rem]", styles.header)}
      >
        <Alert />
        <TopHeader storeConfigServerSide={storeConfigServerSide} />
        <Container
          className="flex min-h-[3.125rem] items-center justify-between relative h-full !px-0"
          rootClassName="h-full min-h-[3.125rem] z-20 border-b-[1px] border-solid border-[#e5e5e5]"
        >
          <div className="hidden items-center justify-center py-5 lg:!flex">
            <UserButton />
            <div className="border-r-[1px] border-[var(--grey-300)] border-solid w-2 mr-2 h-10"></div>
            <Button
              prefixIcon={<SearchIcon width={24} height={24} alt="Search" />}
              shape="square"
              type="borderless"
              onClick={handleOpenSearch}
              aria-label="Search"
              className="!bg-transparent !border-transparent"
            >
              <span className="text-base uppercase font-normal text-[#212121] pt-2">
                Search
              </span>
            </Button>
          </div>
          <div className="flex items-center gap-1 justify-center">
            <Button
              className="flex lg:!hidden !text-black w-fit"
              prefixIcon="menu1"
              shape="square"
              type="borderless"
              onClick={() => setOpenMenu(true)}
              aria-label="Menu"
            />
            <a href="/" className="flex items-center">
              {(storeConfigServerSide ?? storeConfig)?.frontend_config_logo && (
                <Image
                  src={
                    (storeConfigServerSide ?? storeConfig)
                      ?.frontend_config_logo || ""
                  }
                  alt="Logo"
                  className="h-[40px] object-contain"
                  width={300}
                  height={58}
                  loading="eager"
                />
              )}
            </a>
          </div>

          <div
            className="flex min-w-[180px] justify-end "
            style={{
              pointerEvents:
                currentUser?.loading || Cart?.loading ? "none" : "auto",
            }}
          >
            <div className="flex items-center gap-1 justify-center lg:!hidden">
              <Button
                prefixIcon={<SearchIcon width={24} height={24} alt="Search" />}
                shape="square"
                type="borderless"
                onClick={handleOpenSearch}
                aria-label="Search"
                className="!bg-transparent !border-transparent"
              />
              <UserButton />
            </div>
            <WishButton />
            <CartButton />
          </div>
        </Container>

        <div className="bg-white">
          <div className="flex-1 gap-[56px] text-center border-b-[1px] border-solid border-[#e5e5e5]">
            <div className="hidden md:flex flex-1 justify-center">
              <Navigation menu={menu} className="justify-center font-bold" />
            </div>
          </div>
        </div>

        <BottomAlert />

        {isClient && (
          <>
            <SearchFilter
              recentKeywords={recentKeywords?.slice(0, 5)}
              openSearch={openSearch}
              onCloseSearch={() => setOpenSearch(false)}
            />
            <MobileHeaderTwoV2
              open={openMenu}
              onClose={() => setOpenMenu(false)}
            />
          </>
        )}
      </div>
    </Fragment>
  );
};

export default HeaderV2;
